import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, TrendingDown, TrendingUp, DollarSign, RefreshCw, Target } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const RANGES = [
    { label: 'Últimos 7 días', days: 7 },
    { label: 'Últimos 30 días', days: 30 },
    { label: 'Últimos 90 días', days: 90 },
    { label: 'Último año', days: 365 },
];

function fmtCLP(n) {
    if (n === undefined || n === null || Number.isNaN(Number(n))) return '—';
    return '$' + Number(n).toLocaleString('es-CL');
}
function fmtPct(n) {
    if (n === undefined || n === null || Number.isNaN(Number(n))) return '—';
    return `${Number(n).toFixed(1)}%`;
}

export default function CommercialFunnelPanel() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = async (d = days) => {
        try {
            setLoading(true);
            setError(null);
            const { data: res } = await api.get(`/admin/analytics/commercial-funnel?days=${d}`);
            setData(res);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Error al cargar el embudo comercial');
            toast.error('No se pudo cargar el embudo comercial');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

    const maxCount = useMemo(() => {
        if (!data?.stages?.length) return 1;
        return Math.max(1, ...data.stages.map((s) => s.count || 0));
    }, [data]);

    return (
        <Card data-testid="commercial-funnel-panel">
            <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-800">
                            <Target className="h-5 w-5" />
                        </div>
                        <div>
                            <CardTitle className="font-heading text-lg">🎯 Embudo de Conversión Comercial</CardTitle>
                            <CardDescription>
                                Journey completo: <strong>Visita → Registro → 1er Análisis IA → Paywall → Checkout → Pago Aprobado</strong>.
                                Fuente de verdad para pagos: <code>pagos.mp_status=&apos;approved&apos;</code> (webhook Mercado Pago).
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1 text-xs" data-testid="funnel-range-toggle">
                            {RANGES.map((r) => (
                                <button
                                    key={r.days}
                                    onClick={() => setDays(r.days)}
                                    className={`px-2.5 py-1 rounded transition-colors ${days === r.days ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                                    data-testid={`funnel-range-${r.days}`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => load(days)} className="gap-2" data-testid="funnel-refresh-btn">
                            <RefreshCw className="h-3.5 w-3.5" /> Refrescar
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                {loading ? (
                    <div className="p-6 text-center text-slate-500">
                        <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Cargando embudo comercial…
                    </div>
                ) : error ? (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="funnel-error">
                        Error: {String(error)}
                    </div>
                ) : !data ? null : (
                    <>
                        {/* KPIs comerciales */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="funnel-kpis">
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">
                                    <DollarSign className="h-3 w-3" /> Revenue total
                                </div>
                                <div className="mt-0.5 font-mono text-lg font-extrabold text-emerald-900" data-testid="kpi-revenue-total">
                                    {fmtCLP(data.kpis_comerciales?.revenue_total_clp)}
                                </div>
                            </div>
                            <div className="rounded-lg border border-slate-200 p-3">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">ARPU</div>
                                <div className="mt-0.5 font-mono text-lg font-extrabold text-slate-900" data-testid="kpi-arpu">
                                    {fmtCLP(data.kpis_comerciales?.arpu_clp)}
                                </div>
                            </div>
                            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                                <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-bold">Conv. Visita → Pago</div>
                                <div className="mt-0.5 font-mono text-lg font-extrabold text-indigo-900" data-testid="kpi-conv-visita-pago">
                                    {fmtPct(data.kpis_comerciales?.conv_visita_a_pago_pct)}
                                </div>
                            </div>
                            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                                <div className="text-[10px] uppercase tracking-widest text-sky-700 font-bold">Conv. Registro → Pago</div>
                                <div className="mt-0.5 font-mono text-lg font-extrabold text-sky-900" data-testid="kpi-conv-registro-pago">
                                    {fmtPct(data.kpis_comerciales?.conv_registro_a_pago_pct)}
                                </div>
                            </div>
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-700 font-bold">
                                    <TrendingDown className="h-3 w-3" /> Abandono checkout
                                </div>
                                <div className="mt-0.5 font-mono text-lg font-extrabold text-amber-900" data-testid="kpi-abandono-checkout">
                                    {fmtPct(data.kpis_comerciales?.abandono_checkout_pct)}
                                </div>
                            </div>
                            <div className="rounded-lg border border-slate-200 p-3">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Revenue por plan</div>
                                <div className="mt-0.5 text-xs text-slate-800 space-y-0.5" data-testid="kpi-revenue-by-plan">
                                    {Object.entries(data.kpis_comerciales?.revenue_by_plan || {}).length === 0 ? (
                                        <span className="text-slate-400">—</span>
                                    ) : (
                                        Object.entries(data.kpis_comerciales.revenue_by_plan).map(([k, v]) => (
                                            <div key={k} className="flex justify-between gap-2 font-mono">
                                                <span className="text-slate-500">{k}</span>
                                                <span className="font-bold text-emerald-800">{fmtCLP(v)}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Funnel visual + tabla */}
                        <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="funnel-stages-table">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Etapa</th>
                                        <th className="px-3 py-2 text-right">Docentes</th>
                                        <th className="px-3 py-2 text-right">Conv. vs anterior</th>
                                        <th className="px-3 py-2 text-right">Drop-off</th>
                                        <th className="px-3 py-2 text-right">% desde visita</th>
                                        <th className="px-3 py-2 text-left w-[30%]">Visualización</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.stages.map((s, i) => {
                                        const widthPct = maxCount > 0 ? Math.max(2, (s.count / maxCount) * 100) : 0;
                                        const isPago = s.key === 'pago_aprobado';
                                        return (
                                            <tr key={s.key} className={`border-t border-slate-100 ${isPago ? 'bg-emerald-50/40' : ''}`} data-testid={`funnel-row-${s.key}`}>
                                                <td className="px-3 py-2.5 font-semibold text-slate-800">
                                                    {s.label}
                                                    <div className="text-[10px] text-slate-400 font-normal mt-0.5">{s.source}</div>
                                                </td>
                                                <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-900">{Number(s.count).toLocaleString('es-CL')}</td>
                                                <td className="px-3 py-2.5 text-right font-mono text-xs">
                                                    {i === 0 ? (
                                                        <span className="text-slate-400">—</span>
                                                    ) : s.conv_from_prev_pct === null ? (
                                                        <span className="text-slate-300">—</span>
                                                    ) : (
                                                        <Badge className={`text-[11px] ${s.conv_from_prev_pct >= 50 ? 'bg-emerald-100 text-emerald-800' : s.conv_from_prev_pct >= 20 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>
                                                            {fmtPct(s.conv_from_prev_pct)}
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2.5 text-right font-mono text-xs text-rose-700">
                                                    {i === 0 ? '—' : s.drop_from_prev_pct === null ? '—' : (
                                                        <span className="inline-flex items-center gap-0.5">
                                                            <TrendingDown className="h-3 w-3" /> {fmtPct(s.drop_from_prev_pct)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-600">{fmtPct(s.conv_from_visit_pct)}</td>
                                                <td className="px-3 py-2.5">
                                                    <div className="h-3 rounded bg-slate-100 overflow-hidden">
                                                        <div
                                                            className={`h-full ${isPago ? 'bg-emerald-500' : i >= 3 ? 'bg-indigo-500' : 'bg-sky-500'}`}
                                                            style={{ width: `${widthPct}%` }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Cohortes por origen */}
                        {data.cohortes_origen?.length > 0 && (
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="funnel-cohortes-origen">
                                <div className="bg-slate-50 px-3 py-2 text-xs font-bold uppercase text-slate-500 tracking-wide flex items-center gap-2">
                                    <TrendingUp className="h-3.5 w-3.5" /> Cohortes por origen de tráfico (registros)
                                </div>
                                <table className="w-full text-sm">
                                    <thead className="bg-white text-[10px] text-slate-400 uppercase tracking-wide">
                                        <tr>
                                            <th className="px-3 py-1.5 text-left">Fuente</th>
                                            <th className="px-3 py-1.5 text-right">Registros</th>
                                            <th className="px-3 py-1.5 text-right">% del total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.cohortes_origen.map((c) => {
                                            const total = data.cohortes_origen.reduce((a, b) => a + b.registros, 0) || 1;
                                            const pct = (c.registros / total) * 100;
                                            return (
                                                <tr key={c.fuente} className="border-t border-slate-100">
                                                    <td className="px-3 py-1.5 font-mono text-xs text-slate-800">{c.fuente}</td>
                                                    <td className="px-3 py-1.5 text-right font-mono font-bold">{c.registros}</td>
                                                    <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtPct(pct)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Notas */}
                        {data.notas?.length > 0 && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1" data-testid="funnel-notas">
                                <div className="font-bold uppercase tracking-widest text-[10px] text-amber-700">Notas metodológicas</div>
                                <ul className="list-disc pl-4 space-y-0.5">
                                    {data.notas.map((n, i) => <li key={i}>{n}</li>)}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
